/**
 * useLocalStorage Hook
 * A custom hook that persists state to localStorage, allowing state to survive
 * page refreshes and browser sessions.
 *
 * @param key - The localStorage key to store the value under
 * @param initialValue - The initial value if no stored value exists
 * @returns A tuple of [storedValue, setValue] similar to useState
 */
import { useState, useCallback, useRef } from "react";

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
    // Use ref to store initialValue to avoid re-renders when it's an object/array
    const initialValueRef = useRef(initialValue);

    // Get stored value from localStorage or use initial value (lazy initialization)
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof window === "undefined") {
            return initialValueRef.current;
        }

        try {
            const item = window.localStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : initialValueRef.current;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValueRef.current;
        }
    });

    // Custom setter that also updates localStorage
    const setValue = useCallback(
        (value: T | ((prev: T) => T)) => {
            try {
                setStoredValue((prevValue) => {
                    // Allow value to be a function so we have same API as useState
                    const valueToStore = value instanceof Function ? value(prevValue) : value;
                    // Save to localStorage
                    if (typeof window !== "undefined") {
                        window.localStorage.setItem(key, JSON.stringify(valueToStore));
                    }
                    return valueToStore;
                });
            } catch (error) {
                console.warn(`Error setting localStorage key "${key}":`, error);
            }
        },
        [key]
    );

    return [storedValue, setValue];
}

export default useLocalStorage;
